import { ApiError } from 'api/http/types';
import {
  getErrorReasonType,
  isBusinessError,
  isPromoCodeError,
} from 'scenes/common/api/quote';
import { unmarshalQuoteErrorResponse } from 'scenes/common/api/quote/marshaller';
import { WireErrorCode } from 'scenes/common/api/quote/types';
import {
  makeApiError,
  makePromoCodeError,
} from 'scenes/common/helpers/apiData';
import {
  ApiData,
  ApiErrorReasonType,
  ApiState,
  BusinessErrorDetail,
} from 'scenes/common/types';
import analytics from 'scenes/home/analytics';
import { hasPromoCode } from 'scenes/home/selectors';
import {
  HomeFunnelState,
  HomeQuoteDetails,
  ReferenceData,
} from 'scenes/home/types';
import { WireHomeQuotePayload, WireHomeQuoteResponse } from '../types';

const isInvalidPromoCode = (
  funnelState: HomeFunnelState,
  payload: WireHomeQuotePayload
): boolean => hasPromoCode(funnelState) && payload && !payload.campaigns;

export const checkPromoCode = (
  funnelState: HomeFunnelState,
  response: WireHomeQuoteResponse
): WireHomeQuoteResponse | never => {
  if (isInvalidPromoCode(funnelState, response.payload)) {
    throw new ApiError('Invalid promoCode', ApiState.BusinessError, {
      type: ApiErrorReasonType.IneligiblePromoCode,
    });
  }
  return response;
};

const isHomeEmbargo = (details: BusinessErrorDetail): boolean =>
  details.ref === 'REF055' && details.code === WireErrorCode.Declined;

const isDeclinedInvalidSumInsured = (details: BusinessErrorDetail): boolean =>
  details.code === 'BR000203';

const getErrorReason = (
  details: BusinessErrorDetail | undefined
): ApiErrorReasonType => {
  if (!details) {
    return ApiErrorReasonType.Unknown;
  }
  if (isHomeEmbargo(details)) {
    return ApiErrorReasonType.Embargo;
  }
  if (isDeclinedInvalidSumInsured(details)) {
    return ApiErrorReasonType.Declined;
  }
  return getErrorReasonType(details);
};

const getQuoteError = (
  error: ApiError<BusinessErrorDetail>
): [string, ApiErrorReasonType] => [
  error?.data?.id ?? '',
  getErrorReason(error?.data),
];

export const handleError = (
  err: ApiError<BusinessErrorDetail>,
  funnelState: HomeFunnelState,
  referenceData: ReferenceData
): ApiData<HomeQuoteDetails> => {
  if (isBusinessError(err)) {
    const [quoteNumber, reason] = getQuoteError(err);

    analytics().referral(funnelState, reason, referenceData);

    return isPromoCodeError(reason)
      ? makePromoCodeError(
          reason,
          funnelState.apiResponses.quoteDetails.data?.payload.policy.number ??
            quoteNumber,
          funnelState.apiResponses.quoteDetails
        )
      : unmarshalQuoteErrorResponse(reason, quoteNumber, true, '');
  }
  return makeApiError(err.message, funnelState.apiResponses.quoteDetails);
};
